import { useCallback, useEffect, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useToggle } from '@direct-frontend/stdlib';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { type OpenApiSchemaSectionTemplate, type Section } from '#shared/api/schema';
import { useYandexKit } from '#shared/components/YandexKitProvider';
import { CONSTRUCTOR_KEY, persistentStorage } from '#shared/lib/storage';
import { clearConstructorPageTemplatesCache } from '../api';
import { updateConstructorContent } from '../api/constructor/api';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import { prepareConstructorSectionForSave } from '../lib/prepareConstructorSectionForSave';
import { CONSTRUCTOR_MINIMUM_SECTION_SEQUENCE } from '../model/constants';
import {
    changeActiveVersion,
    changeConstructorIsDirty,
    finishAppliedTemplateDraft,
    getConstructorActiveVersion,
    useConstructorIsDirty,
    useConstructorIsFirstTime,
    useConstructorPagesSettings,
    useConstructorPreviewGlobalSettings,
} from '../model/slices';

export const useSaveConstructor = () => {
    const yandexKit = useYandexKit();
    const allPages = useConstructorPagesSettings(true);
    const globalSettings = useConstructorPreviewGlobalSettings();
    const isFirstTime = useConstructorIsFirstTime();
    const isDirty = useConstructorIsDirty();
    const { value: isSaving, toggleOn: startSaving, toggleOff: endSaving } = useToggle();

    const pages = useMemo(
        () =>
            Object.values(allPages).map((page) => {
                return {
                    ...page,
                    layout: Object.values(page.layout)
                        .reduce(
                            (
                                layout: (Section & { template?: OpenApiSchemaSectionTemplate })[],
                                section: Section & { template?: OpenApiSchemaSectionTemplate },
                                index
                            ) => {
                                const settings = yandexKit.getSettings(section.widget);
                                const templateWidgetSettings =
                                    'template' in section &&
                                    section.template?.alias &&
                                    yandexKit.getSettings(section.template.alias);

                                if (typeof settings?.displaySequence === 'number') {
                                    section.display_sequence = settings.displaySequence;
                                } else if (
                                    templateWidgetSettings &&
                                    typeof templateWidgetSettings?.displaySequence === 'number'
                                ) {
                                    section.display_sequence = templateWidgetSettings.displaySequence;
                                } else {
                                    section.display_sequence = index + CONSTRUCTOR_MINIMUM_SECTION_SEQUENCE;
                                }

                                layout.push(section);

                                return layout;
                            },
                            []
                        )
                        // вторая сортировка, чтобы константные значения встали куда надо
                        .sort((a, b) => a.display_sequence - b.display_sequence)
                        .map((section) => prepareConstructorSectionForSave(section, yandexKit.getSettings(section))),
                    static_context: {},
                };
            }),
        [allPages, yandexKit]
    );

    const save = useCallback(async () => {
        if (isEmpty(globalSettings)) {
            throw new Error('Недостаточно данных для сохранения');
        }

        startSaving();

        try {
            const newVersion = await updateConstructorContent({ pages, global_settings: globalSettings });

            finishAppliedTemplateDraft();
            changeConstructorIsDirty(false);
            changeActiveVersion(newVersion.version.id);
            persistentStorage.removeItem(CONSTRUCTOR_KEY);
            logEventString(constructorProductLoggingEvents.LocalSave.ClearedOnSave, 'true');

            clearConstructorPageTemplatesCache();
        } finally {
            endSaving();
        }
    }, [pages, globalSettings, startSaving, endSaving]);

    useEffect(() => {
        if (isFirstTime) {
            return;
        }

        if (pages.length > 0 && isDirty) {
            persistentStorage.setItem(
                CONSTRUCTOR_KEY,
                JSON.stringify({
                    pages,
                    globalSettings,
                    date: Date.now(),
                    versionId: getConstructorActiveVersion(),
                })
            );
        }
    }, [pages, globalSettings, isFirstTime, isDirty]);

    return { save, isSaving };
};
